import { ChangeDetectionStrategy, Component, inject, OnInit } from '@angular/core';
import { TranslocoDirective } from '@jsverse/transloco';
import { LoaderComponent } from '@modules/shared/loader/loader.component';
import { Store } from '@ngrx/store';
import { Media } from '@pixacare/domain';
import { MediaHttpService } from '@services/http/media.http.service';
import { selectSequence } from '@shared/store/sequences/sequences.selectors';
import { TuiDialogContext } from '@taiga-ui/core';
import { POLYMORPHEUS_CONTEXT } from '@taiga-ui/polymorpheus';
import { combineLatest, first, map, Observable, of, switchMap } from 'rxjs';
import {
  WoundtrackInputsModuleComponentInputs,
  WoundtrackInputsModuleComponentOutputs,
} from '../models/woundtrack-inputs-module-component';

@Component({
  selector: 'pxc-woundtrack-inputs',
  imports: [LoaderComponent, TranslocoDirective],
  templateUrl: './woundtrack-inputs.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  host: {
    class: 'h-full w-full py-6',
  },
})
export class WoundtrackInputsComponent implements OnInit {
  private readonly mediaService = inject(MediaHttpService);
  private readonly store = inject(Store);
  protected readonly context =
    inject<TuiDialogContext<WoundtrackInputsModuleComponentOutputs, WoundtrackInputsModuleComponentInputs>>(
      POLYMORPHEUS_CONTEXT,
    );

  files: WoundtrackInputsModuleComponentInputs['files'] = this.context.data?.files;
  medias: WoundtrackInputsModuleComponentInputs['medias'] = this.context.data?.medias;
  sequenceId: WoundtrackInputsModuleComponentInputs['sequenceId'] = this.context.data?.sequenceId;

  get woundtrackInputs$(): Observable<{ pictures: (Media | File)[] }> {
    return combineLatest({
      files: !this.files?.length ? (of([]) as Observable<File[]>) : combineLatest(this.files.map((file) => of(file))),
      medias: !this.medias?.length
        ? (of([]) as Observable<Media[]>)
        : combineLatest(this.medias.map((media) => this.getMedia(media))),
      sequenceMedias: !this.sequenceId
        ? (of([]) as Observable<Media[]>)
        : this.store.select(selectSequence(this.sequenceId)).pipe(
            map((sequence) => sequence.medias),
            switchMap((medias) => combineLatest(medias.map((media) => this.getMedia(media)))),
          ),
    }).pipe(
      map(({ files, medias, sequenceMedias }) => ({
        pictures: [...sequenceMedias, ...medias, ...files],
      })),
    );
  }

  ngOnInit(): void {
    this.woundtrackInputs$.pipe(first()).subscribe({
      next: ({ pictures }) => {
        this.context.completeWith({ pictures });
      },
    });
  }

  private getMedia(media: Media): Observable<Media> {
    return media.uri ? of(media) : this.mediaService.getMedia(media.clientCode, media.id);
  }
}
